/* AUTO-SYNCED from PsyKit.io/hooks/use-webrtc-peer.ts — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

/**
 * hooks/use-webrtc-peer.ts — the 1:1 video core (Phase 5).
 *
 * One RTCPeerConnection driving therapist↔client video over the SAME Socket.IO
 * `bilateral:<code>` room the BLS hooks already use. Implements the MDN
 * "perfect negotiation" state machine (glare resolved by lib/rtc/perfect-
 * negotiation). Layout-agnostic — it owns no DOM, only streams + status, so the
 * therapist page and the client /view share it.
 *
 * ── HARD INVARIANT ──────────────────────────────────────────────────────────
 *   This hook MUST NOT emit "join" or "leave". Room membership belongs solely
 *   to useBilateralBroadcast / useBilateralMirror. A stray "leave" here would
 *   silently kill BLS sync for the whole session. We only ever touch our own
 *   rtc_* events and depend on a BLS hook already having joined the room.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Mirrors the getSocket() + socket.on/off discipline of use-bilateral-mirror.ts.
 * Reconnection/ICE-restart hardening and iOS specifics are deliberately left to
 * Phases 10/11; this exposes the full control surface but keeps the internals
 * to: negotiate, trickle ICE, render two streams, tear down cleanly.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getSocket } from "@/lib/socket-client";
import { shouldIgnoreOffer } from "@/lib/rtc/perfect-negotiation";

export interface WebrtcPeerOptions {
  /** Master switch. The PC exists only while true; flipping false tears down. */
  enabled: boolean;
  /** Therapist = true. Drives the first offer and re-offers when the peer rejoins. */
  isInitiator: boolean;
  /** Anonymous client = true. The polite peer yields on offer glare. */
  isPolite: boolean;
  /** ICE servers. Therapist: from /api/rtc/credentials. Client: relayed via rtc_ice_config. */
  iceServers?: RTCIceServer[];
  /**
   * Pre-acquired local media. Phase 8 (preflight) hands this in so we don't
   * prompt twice. If omitted, the hook calls getUserMedia itself. We only stop
   * tracks we acquired ourselves — a preflight-owned stream is the caller's.
   */
  localStream?: MediaStream | null;
}

export type CallStatus =
  | "idle"
  | "acquiring"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "failed";

export interface WebrtcPeerState {
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  status: CallStatus;
  connectionState: RTCPeerConnectionState | null;
  iceConnectionState: RTCIceConnectionState | null;
  error: string | null;
}

export interface WebrtcPeerControls {
  /** Full reconnect: tear down and rebuild the PC with the latest iceServers. */
  retry: () => void;
  /** Flip the front/back camera (basic replaceTrack; Phase 11 hardens for iOS). */
  switchCamera: () => void;
  /** Hang up: notify the peer + stop our camera now. Parent should also unset `enabled`. */
  endCall: () => void;
}

/**
 * DEFAULT capture constraints (decision point — tune for clinical use):
 * 720p/24fps talking-heads, with the audio cleanup a therapy call wants. 720p
 * is a deliberate ceiling: it's plenty for reading facial affect, and TURN-
 * relayed calls are metered, so we don't ship 1080p down a paid relay. `ideal`
 * (not `exact`) lets weak devices/iOS degrade instead of failing acquisition.
 */
const MEDIA_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 24, max: 30 },
    facingMode: "user",
  },
  audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

/** How many times a "failed" PC auto-rebuilds before we surface a manual Retry. */
const MAX_AUTO_RETRIES = 3;

function mediaErrorMessage(err: unknown): string {
  switch ((err as { name?: string } | null)?.name) {
    case "NotAllowedError":
      return "Camera and microphone permission was denied.";
    case "NotFoundError":
      return "No camera or microphone was found.";
    case "NotReadableError":
      return "Your camera or microphone is already in use by another app.";
    default:
      return "Could not start video.";
  }
}

export function useWebrtcPeer(opts: WebrtcPeerOptions): WebrtcPeerState & WebrtcPeerControls {
  const { enabled, isInitiator, isPolite } = opts;

  // Latest prop values, read by the long-lived effect without re-subscribing it.
  // (iceServers/localStream are fresh array/object refs each render; depending on
  //  them directly would thrash the PC. Changing them takes effect on next retry.)
  const iceServersRef = useRef<RTCIceServer[]>(opts.iceServers ?? []);
  iceServersRef.current = opts.iceServers ?? [];
  const providedStreamRef = useRef<MediaStream | null>(opts.localStream ?? null);
  providedStreamRef.current = opts.localStream ?? null;
  const isInitiatorRef = useRef(isInitiator);
  isInitiatorRef.current = isInitiator;
  const isPoliteRef = useRef(isPolite);
  isPoliteRef.current = isPolite;

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [status, setStatus] = useState<CallStatus>("idle");
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | null>(null);
  const [iceConnectionState, setIceConnectionState] = useState<RTCIceConnectionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  // Imperative handles the async negotiation closures + controls share.
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const ownsStreamRef = useRef(false); // did WE getUserMedia it? only then do we stop it
  const facingModeRef = useRef<"user" | "environment">("user");
  // Perfect-negotiation flags (refs so async handlers read live values).
  const makingOfferRef = useRef(false);
  const isSettingRemoteAnswerPendingRef = useRef(false);
  const ignoreOfferRef = useRef(false);
  // Presence handshake — the fix for the empty-room deadlock. Each side emits
  // rtc_signal {ready:true} when its video PC comes up; we only send an offer
  // once we know the peer is present (peerReadyRef), deferring via
  // pendingNegotiationRef otherwise. Without this the therapist offers into an
  // empty room, strands in have-local-offer, then impolitely ignores the
  // client's later offer — and the call silently never connects.
  const peerReadyRef = useRef(false);
  const pendingNegotiationRef = useRef(false);
  // Self-healing (Phase 10): a brief "disconnected" arms an ICE-restart timer; a
  // hard "failed" rebuilds the PC a bounded number of times. Without this any
  // transient blip (wifi power-save, NAT rebind, screen-lock) stranded the call in
  // "reconnecting" forever, waiting for a manual Retry tap.
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRetriesRef = useRef(0);

  /** Imperative teardown shared by effect-cleanup, endCall, and retry. */
  const teardown = useCallback((notifyPeer: boolean) => {
    if (notifyPeer) {
      // best-effort "I'm hanging up" — NEVER "leave" (that's the BLS hooks' room)
      try {
        getSocket().emit("rtc_bye", {});
      } catch {
        /* socket may be gone on unmount — ignore */
      }
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    const pc = pcRef.current;
    if (pc) {
      pc.onnegotiationneeded = null;
      pc.onicecandidate = null;
      pc.ontrack = null;
      pc.onconnectionstatechange = null;
      pc.oniceconnectionstatechange = null;
      pc.close();
    }
    pcRef.current = null;
    if (ownsStreamRef.current && localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop()); // turns the camera light off
    }
    localStreamRef.current = null;
    ownsStreamRef.current = false;
    makingOfferRef.current = false;
    isSettingRemoteAnswerPendingRef.current = false;
    ignoreOfferRef.current = false;
    peerReadyRef.current = false;
    pendingNegotiationRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const socket = getSocket();
    // `cancelled` guards the async getUserMedia against StrictMode's
    // mount→unmount→mount double-invoke (and against disable mid-acquire).
    let cancelled = false;

    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    pcRef.current = pc;
    setStatus("connecting");
    setError(null);

    const emitSignal = (data: Record<string, unknown>) => socket.emit("rtc_signal", data);

    /** Produce and send an offer — only to a peer we know is present, and never
     *  over an unstable PC. If the peer's video PC isn't here yet, defer until
     *  their ready/SDP arrives (so we never offer into an empty room). */
    const negotiate = async () => {
      if (makingOfferRef.current || pc.signalingState !== "stable") return;
      if (!peerReadyRef.current) {
        pendingNegotiationRef.current = true;
        return;
      }
      try {
        makingOfferRef.current = true;
        await pc.setLocalDescription();
        if (cancelled) return;
        emitSignal({ sdp: pc.localDescription });
      } catch (err) {
        console.error("[webrtc] negotiate failed", (err as Error)?.name);
      } finally {
        makingOfferRef.current = false;
      }
    };

    pc.onnegotiationneeded = () => {
      void negotiate();
    };

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) emitSignal({ candidate });
    };

    pc.ontrack = ({ streams }) => {
      setRemoteStream(streams[0] ?? null);
    };

    pc.onconnectionstatechange = () => {
      setConnectionState(pc.connectionState);
      switch (pc.connectionState) {
        case "connected":
          autoRetriesRef.current = 0; // healthy — refill the recovery budget
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
          setStatus("connected");
          break;
        case "disconnected":
          // Usually a transient blip. Give it ~3s to self-recover; if it hasn't,
          // force an ICE restart (fresh candidates over the SAME signaling channel).
          // EITHER side can be the one that dropped (one-way media loss: e.g. the
          // guest stops receiving the host while the host still sees the guest and
          // stays "connected"), so both arm the timer — but only the initiator can
          // cleanly restartIce(); the polite peer NUDGES the initiator to do it.
          setStatus("reconnecting");
          if (!reconnectTimerRef.current) {
            reconnectTimerRef.current = setTimeout(() => {
              reconnectTimerRef.current = null;
              if (pcRef.current !== pc || pc.connectionState === "connected") return;
              if (isInitiatorRef.current) {
                try {
                  pc.restartIce();
                } catch {
                  /* unsupported → the eventual "failed" path rebuilds the PC */
                }
              } else {
                emitSignal({ renegotiate: true }); // ask the initiator to restart ICE
              }
            }, 3000);
          }
          break;
        case "failed":
          // ICE restart didn't recover (or we're the polite peer). Rebuild the PC a
          // bounded number of times before surfacing failure for a manual Retry.
          if (autoRetriesRef.current < MAX_AUTO_RETRIES) {
            autoRetriesRef.current += 1;
            setStatus("reconnecting");
            setRetryNonce((n) => n + 1); // rebuilds with iceServersRef.current
          } else {
            setStatus("failed");
          }
          break;
        default:
          break;
      }
    };

    pc.oniceconnectionstatechange = () => {
      setIceConnectionState(pc.iceConnectionState);
      // Belt-and-braces: some engines leave connectionState at "connecting" while
      // media flows (seen on a real 2-device call: healthy video, tile stuck on
      // "Connecting…"). ICE connected/completed IS a working call — reflect it in
      // status. The connectionState handler above still owns the disconnected/
      // failed transitions, so this only ever ADDS the missing happy-path signal.
      if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
        autoRetriesRef.current = 0;
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
        setStatus("connected");
      }
    };

    // ── inbound signaling (relayed by server.ts to everyone else in the room) ──
    const handleSignal = async (payload: {
      sdp?: RTCSessionDescriptionInit;
      candidate?: RTCIceCandidateInit;
      ready?: boolean;
      renegotiate?: boolean;
    }) => {
      if (pcRef.current !== pc) return; // stale handler after teardown
      // Presence ping — the peer's video PC just came up. Mark them present and
      // flush any offer we deferred while the room had no video peer.
      if (payload.ready) {
        peerReadyRef.current = true;
        if (pendingNegotiationRef.current) {
          pendingNegotiationRef.current = false;
          void negotiate();
        }
        return;
      }
      // The polite peer lost its media and asked us to recover. Only the initiator
      // can cleanly restart ICE; doing so re-offers with fresh candidates and heals
      // a one-way drop the initiator's own state never flagged.
      if (payload.renegotiate) {
        if (isInitiatorRef.current) {
          try {
            pc.restartIce();
          } catch {
            /* unsupported → peer's "failed" path rebuilds */
          }
        }
        return;
      }
      try {
        if (payload.sdp) {
          peerReadyRef.current = true; // receiving an SDP proves the peer is here
          const description = payload.sdp;
          const ignore = shouldIgnoreOffer({
            polite: isPoliteRef.current,
            makingOffer: makingOfferRef.current,
            signalingState: pc.signalingState,
            isSettingRemoteAnswerPending: isSettingRemoteAnswerPendingRef.current,
            descriptionType: description.type,
          });
          ignoreOfferRef.current = ignore;
          if (ignore) return;

          isSettingRemoteAnswerPendingRef.current = description.type === "answer";
          await pc.setRemoteDescription(description);
          isSettingRemoteAnswerPendingRef.current = false;
          if (cancelled) return;

          if (description.type === "offer") {
            await pc.setLocalDescription();
            if (cancelled) return;
            emitSignal({ sdp: pc.localDescription });
          }
        } else if (payload.candidate) {
          try {
            await pc.addIceCandidate(payload.candidate);
          } catch (err) {
            if (!ignoreOfferRef.current) throw err; // swallow candidates for an ignored offer
          }
        }
      } catch (err) {
        console.error("[webrtc] signal handling failed", (err as Error)?.name);
      }
    };

    // Peer hung up → drop their video; keep our own self-view alive.
    const handleBye = () => {
      if (pcRef.current !== pc) return;
      setRemoteStream(null);
    };

    // A socket (re)joined the bilateral room (server sync_request). Re-announce
    // our presence so a reconnecting peer re-triggers negotiation. Offers are
    // driven by the ready handshake (robust to empty-room ordering), so we ping
    // rather than re-offer directly. Only the initiator pings, to avoid dupes.
    const handleSyncRequest = () => {
      if (pcRef.current !== pc) return;
      if (isInitiatorRef.current) emitSignal({ ready: true });
    };

    socket.on("rtc_signal", handleSignal);
    socket.on("rtc_bye", handleBye);
    socket.on("sync_request", handleSyncRequest);

    // Announce our video PC's presence. If the peer is already enabled, they'll
    // flush any deferred offer; and we'll offer once we hear their ready back.
    emitSignal({ ready: true });

    // ── acquire local media, then add tracks (which fires negotiationneeded) ──
    const start = async () => {
      try {
        let stream = providedStreamRef.current;
        if (!stream) {
          setStatus("acquiring");
          stream = await navigator.mediaDevices.getUserMedia(MEDIA_CONSTRAINTS);
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop()); // unmounted mid-acquire
            return;
          }
          ownsStreamRef.current = true;
        }
        localStreamRef.current = stream;
        setLocalStream(stream);
        setStatus("connecting");
        for (const track of stream.getTracks()) pc.addTrack(track, stream);
        // If the peer is already here, our offer goes out via negotiationneeded.
        // If not, handleSyncRequest re-offers when they arrive.
      } catch (err) {
        if (cancelled) return;
        setError(mediaErrorMessage(err));
        setStatus("failed");
      }
    };
    void start();

    return () => {
      cancelled = true;
      socket.off("rtc_signal", handleSignal);
      socket.off("rtc_bye", handleBye);
      socket.off("sync_request", handleSyncRequest);
      teardown(true);
      setLocalStream(null);
      setRemoteStream(null);
      setConnectionState(null);
      setIceConnectionState(null);
      setStatus("idle");
    };
  }, [enabled, retryNonce, teardown]);

  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);

  const endCall = useCallback(() => {
    teardown(true);
    setLocalStream(null);
    setRemoteStream(null);
    setConnectionState(null);
    setIceConnectionState(null);
    setStatus("idle");
  }, [teardown]);

  // Basic front/back flip via replaceTrack (no renegotiation). Phase 11 adds the
  // iOS H.264 + facingMode robustness; here it's a best-effort convenience.
  const switchCamera = useCallback(() => {
    const pc = pcRef.current;
    const current = localStreamRef.current;
    if (!pc || !current || !ownsStreamRef.current) return;
    const next = facingModeRef.current === "user" ? "environment" : "user";
    void (async () => {
      try {
        const cam = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: next },
          audio: false,
        });
        // The call may have been torn down (endCall/disable/retry) during the
        // await — if so, stop the just-acquired camera instead of attaching it
        // to a closed PC / orphaned stream (would leave the camera light on).
        if (pcRef.current !== pc || localStreamRef.current !== current) {
          cam.getTracks().forEach((t) => t.stop());
          return;
        }
        const newTrack = cam.getVideoTracks()[0];
        if (!newTrack) return;
        const sender = pc.getSenders().find((s) => s.track?.kind === "video");
        await sender?.replaceTrack(newTrack);
        // swap the track inside the exposed stream + stop the old camera
        const oldTrack = current.getVideoTracks()[0];
        if (oldTrack) {
          current.removeTrack(oldTrack);
          oldTrack.stop();
        }
        current.addTrack(newTrack);
        facingModeRef.current = next;
        setLocalStream(new MediaStream(current.getTracks())); // new ref → re-render
      } catch (err) {
        console.error("[webrtc] switchCamera failed", (err as Error)?.name);
      }
    })();
  }, []);

  return {
    localStream,
    remoteStream,
    status,
    connectionState,
    iceConnectionState,
    error,
    retry,
    switchCamera,
    endCall,
  };
}
